// src/Components/Dashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Backdrop,
  Paper,
  Chip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SavingsIcon from "@mui/icons-material/Savings";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { addDoc, collection } from "firebase/firestore";
import { dbFirebase } from "../Conections/Firebase";
import { fetchCollection } from "../Utils/firestore";
import { formatearNumero } from "../Utils/formatUtils";
import { getCurrentDateTime } from "../Utils/dateUtils";
import { exportToCsv } from "../Utils/exportUtils";
import { computeDeudoresConEstado } from "../Utils/deudorUtils";
import { buildLoanView } from "../Utils/loanUtils";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
  Tooltip,
  Legend,
} from "recharts";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { useTheme } from "@mui/material/styles";
import NavbarProject from "./NavbarProject";
import TableEdit from "./TableEdit";

const sectionCardSx = {
  borderRadius: 4,
  boxShadow: "0 18px 40px rgba(16, 24, 40, 0.08)",
  border: "1px solid rgba(15, 23, 42, 0.06)",
};

const filtrarPorRango = (items, rango, getFecha = (item) => item.fecha) => {
  const inicio = rango.inicio && dayjs(rango.inicio).isValid() ? dayjs(rango.inicio) : null;
  const fin = rango.fin && dayjs(rango.fin).isValid() ? dayjs(rango.fin) : null;

  return items.filter((item) => {
    const f = dayjs(getFecha(item));
    if (!inicio && !fin) return true;
    if (inicio && fin) {
      const ts = f.valueOf();
      return ts >= inicio.valueOf() && ts <= fin.valueOf();
    }
    if (inicio) return f.valueOf() >= inicio.valueOf();
    if (fin) return f.valueOf() <= fin.valueOf();
    return true;
  });
};

const initialObjInversion = {
  fecha: getCurrentDateTime(),
  cantidad: 0,
  raiz: "",
  tipo: "",
  descripcion: "",
};

const initialRendimientoForm = {
  raiz: "",
  totalActual: "",
  fecha: getCurrentDateTime(),
  descripcion: "",
};

const Dashboard = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    saldoNeto: 0,
    ingresos: 0,
    egresos: 0,
    movimientos: 0,
  });
  const [estadoActual, setEstadoActual] = useState({
    carteraPorCobrar: 0,
    deudaPorPagar: 0,
    posicionNeta: 0,
  });
  const [evolucion, setEvolucion] = useState([]);
  const [lugaresInversion, setLugaresInversion] = useState([]);
  const [categoriasRango, setCategoriasRango] = useState([]);
  const [selectedCats, setSelectedCats] = useState([]);

  // Datos crudos para recalcular con filtros sin ir a la red
  const [inversionesRaw, setInversionesRaw] = useState([]);
  const [categoriaNombres, setCategoriaNombres] = useState({});
  const [categoriasList, setCategoriasList] = useState([]);

  // Filtros de fecha
  const [preset, setPreset] = useState("ytd"); // 'mes', '3m', '6m', '12m', 'ytd', 'todo', 'custom'
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");

  // Alta de movimiento
  const [openModal, setOpenModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [objInversion, setObjInversion] = useState(initialObjInversion);

  // Alta de rendimiento (plusvalía/minusvalía por ajuste de valuación)
  const [openRendimientoModal, setOpenRendimientoModal] = useState(false);
  const [rendimientoForm, setRendimientoForm] = useState(initialRendimientoForm);

  const fetchData = async () => {
    setLoading(true);

    const [
      categorias,
      inversionesRawData,
      deudoresData,
      prestamosData,
      prestamosCajaData,
      prestamosCajaPagosData,
    ] = await Promise.all([
      fetchCollection("lista_inversion"),
      fetchCollection("inversiones"),
      fetchCollection("deudores"),
      fetchCollection("prestamos"),
      fetchCollection("prestamos_caja"),
      fetchCollection("prestamos_caja_pagos"),
    ]);

    const catNames = {};
    categorias.forEach((cat) => {
      catNames[cat.id] = cat.nombre || cat.id;
    });

    const inversiones = inversionesRawData.map((data) => {
      const cantidad = Number(data.cantidad) || 0;
      const tipo = data.tipo || "";
      const raiz = data.raiz || "sin_categoria";
      const fecha =
        data.fecha &&
        typeof data.fecha === "object" &&
        typeof data.fecha.toDate === "function"
          ? data.fecha.toDate()
          : data.fecha;
      return {
        id: data.id,
        cantidad,
        tipo,
        raiz,
        fecha,
        descripcion: data.descripcion || "",
        esRendimiento: Boolean(data.esRendimiento),
      };
    });

    const deudoresConEstado = computeDeudoresConEstado(deudoresData, prestamosData);
    const carteraPorCobrar = deudoresConEstado
      .filter((d) => d.prestamoActivo)
      .reduce((acc, d) => acc + d.restante, 0);

    const prestamosCajaView = prestamosCajaData.map((prestamo) =>
      buildLoanView(
        prestamo,
        prestamosCajaPagosData.filter((p) => p.prestamoId === prestamo.id)
      )
    );
    const deudaPorPagar = prestamosCajaView.reduce(
      (acc, p) => acc + (p.resumen.saldoActual || 0),
      0
    );

    setCategoriaNombres(catNames);
    setCategoriasList(categorias);
    setInversionesRaw(inversiones);
    setEstadoActual({
      carteraPorCobrar,
      deudaPorPagar,
      posicionNeta: carteraPorCobrar - deudaPorPagar,
    });
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Calcular rango de fechas a partir del preset o personalizado
  const rango = useMemo(() => {
    const hoy = dayjs();
    let inicio = null;
    let fin = null;
    switch (preset) {
      case "mes":
        inicio = hoy.startOf("month");
        fin = hoy.endOf("month");
        break;
      case "3m":
        inicio = hoy.subtract(3, "month").startOf("day");
        fin = hoy.endOf("day");
        break;
      case "6m":
        inicio = hoy.subtract(6, "month").startOf("day");
        fin = hoy.endOf("day");
        break;
      case "12m":
        inicio = hoy.subtract(12, "month").startOf("day");
        fin = hoy.endOf("day");
        break;
      case "ytd":
        inicio = hoy.startOf("year");
        fin = hoy.endOf("day");
        break;
      case "todo":
        inicio = null;
        fin = null;
        break;
      case "custom":
        inicio = fechaInicio ? dayjs(fechaInicio) : null;
        fin = fechaFin ? dayjs(fechaFin).endOf("day") : null;
        break;
      default:
        inicio = null;
        fin = null;
    }
    return { inicio, fin };
  }, [preset, fechaInicio, fechaFin]);

  // La selección manual de "disposición inmediata" no tiene sentido de un rango a otro
  useEffect(() => {
    setSelectedCats([]);
  }, [rango]);

  // Dataset mensual para barras Ingreso/Egreso (flujo de caja real, sin ajustes de valuación)
  const datasetMensual = useMemo(() => {
    const mensual = {};

    filtrarPorRango(inversionesRaw, rango)
      .filter((m) => !m.esRendimiento)
      .forEach((m) => {
        const mes = dayjs(m.fecha).format("YYYY-MM");
        if (!mensual[mes]) mensual[mes] = { mes, Ingreso: 0, Egreso: 0 };
        if (m.tipo === "INGRESO") mensual[mes].Ingreso += m.cantidad;
        else if (m.tipo === "EGRESO") mensual[mes].Egreso += m.cantidad;
      });

    return Object.values(mensual).sort((a, b) => (a.mes > b.mes ? 1 : -1));
  }, [inversionesRaw, rango]);

  // Total histórico por inversión (sin filtro de rango), usado para comparar contra el total real
  // reportado por el banco/broker y calcular la plusvalía o minusvalía.
  const totalesPorRaiz = useMemo(() => {
    const map = {};
    inversionesRaw.forEach((m) => {
      if (!map[m.raiz]) map[m.raiz] = 0;
      map[m.raiz] += m.tipo === "INGRESO" ? m.cantidad : -m.cantidad;
    });
    return map;
  }, [inversionesRaw]);

  // Movimientos del rango seleccionado, listos para la tabla del libro de inversiones
  const movimientosFiltrados = useMemo(() => {
    return filtrarPorRango(inversionesRaw, rango)
      .slice()
      .sort((a, b) => dayjs(b.fecha).valueOf() - dayjs(a.fecha).valueOf())
      .map((m) => ({
        ...m,
        lugar: categoriaNombres[m.raiz] || m.raiz,
        fechaFormateada: dayjs(m.fecha).format("YYYY-MM-DD HH:mm"),
      }));
  }, [inversionesRaw, rango, categoriaNombres]);

  // Derivar métricas y datasets según rango
  useEffect(() => {
    if (!inversionesRaw.length) return;

    const filtrados = filtrarPorRango(inversionesRaw, rango);
    // El flujo de caja real excluye los ajustes de rendimiento (plusvalía/minusvalía),
    // que no son dinero que entró o salió sino un ajuste de valuación.
    const filtradosFlujo = filtrados.filter((m) => !m.esRendimiento);

    // KPIs
    const ingresos = filtradosFlujo
      .filter((m) => m.tipo === "INGRESO")
      .reduce((a, b) => a + b.cantidad, 0);
    const egresos = filtradosFlujo
      .filter((m) => m.tipo === "EGRESO")
      .reduce((a, b) => a + b.cantidad, 0);
    const saldoNeto = ingresos - egresos;
    setStats({ ingresos, egresos, saldoNeto, movimientos: filtrados.length });

    // El saldo por lugar sí incluye los ajustes de rendimiento, porque refleja el valor
    // real de cada inversión (incluyendo plusvalía/minusvalía), no solo el flujo de caja.
    const catMap = {};
    filtrados.forEach((m) => {
      if (!catMap[m.raiz]) catMap[m.raiz] = 0;
      catMap[m.raiz] += m.tipo === "INGRESO" ? m.cantidad : -m.cantidad;
    });
    const lugares = Object.entries(catMap)
      .map(([id, cant]) => ({
        lugar: categoriaNombres[id] || id,
        cantidad: cant,
      }))
      .sort((a, b) => Math.abs(b.cantidad) - Math.abs(a.cantidad));
    setLugaresInversion(lugares);

    // Lista de categorías en el rango con su neto para selección manual
    const categoriasLista = Object.entries(catMap)
      .map(([id, neto]) => ({ id, nombre: categoriaNombres[id] || id, neto }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
    setCategoriasRango(categoriasLista);

    // Evolución mensual-barras (ingreso/egreso mensuales, flujo de caja real)
    const mensual = {};
    filtradosFlujo.forEach((m) => {
      const mes = dayjs(m.fecha).format("YYYY-MM");
      if (!mensual[mes]) mensual[mes] = { mes, Ingreso: 0, Egreso: 0 };
      if (m.tipo === "INGRESO") mensual[mes].Ingreso += m.cantidad;
      else if (m.tipo === "EGRESO") mensual[mes].Egreso += m.cantidad;

      if (mes === "Invalid Date") {
        console.log("Fecha inválida en movimiento:", mes, m);
      }
    });
    const mensualArr = Object.values(mensual).sort((a, b) =>
      a.mes > b.mes ? 1 : -1
    );

    // Linea de acumulado neto
    let acum = 0;
    const toMesLargo = (mesYYYYMM) => {
      const [y, m] = mesYYYYMM.split("-");
      const date = new Date(Number(y), Number(m) - 1, 1);

      // "enero", "febrero", etc.
      const mesNombre = date.toLocaleString("es-MX", { month: "long" });

      // Capitalizar: "Enero"
      const mesCap = mesNombre.charAt(0).toUpperCase() + mesNombre.slice(1);

      return `${mesCap} ${y}`;
    };

    const evol = mensualArr.map((m) => {
      const neto = m.Ingreso - m.Egreso;
      acum += neto;
      return { mes: toMesLargo(m.mes), saldoNeto: parseFloat(acum.toFixed(2)) };
    });
    setEvolucion(evol);
  }, [inversionesRaw, rango, categoriaNombres]);

  const StatCard = ({ label, value, helper, icon, color, bg }) => (
    <Card sx={{ ...sectionCardSx, background: isDarkMode ? undefined : bg, height: "100%" }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 3,
            display: "grid",
            placeItems: "center",
            mb: 2,
            color,
            bgcolor: "rgba(255,255,255,0.72)",
          }}
        >
          {icon}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 700, color: "text.primary" }}>
          {value}
        </Typography>
        {helper && (
          <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
            {helper}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const statCards = [
    {
      label: "Saldo Neto",
      value: formatearNumero(stats.saldoNeto),
      helper: stats.saldoNeto >= 0 ? "Superávit en el rango" : "Déficit en el rango",
      icon: <TrendingUpIcon />,
      color: "#0f766e",
      bg: "linear-gradient(135deg, #ecfeff 0%, #f0fdfa 100%)",
    },
    {
      label: "Ingresos",
      value: formatearNumero(stats.ingresos),
      helper: "En el rango seleccionado",
      icon: <ArrowUpwardIcon />,
      color: "#15803d",
      bg: "linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)",
    },
    {
      label: "Egresos",
      value: formatearNumero(stats.egresos),
      helper: "En el rango seleccionado",
      icon: <ArrowDownwardIcon />,
      color: "#b91c1c",
      bg: "linear-gradient(135deg, #fef2f2 0%, #fff1f2 100%)",
    },
    {
      label: "Movimientos",
      value: `${stats.movimientos}`,
      helper: "Registros en el rango",
      icon: <ReceiptLongIcon />,
      color: "#1d4ed8",
      bg: "linear-gradient(135deg, #eff6ff 0%, #eef2ff 100%)",
    },
  ];

  const estadoCards = [
    {
      label: "Cartera por cobrar",
      value: formatearNumero(estadoActual.carteraPorCobrar),
      helper: "Deudores con préstamo activo",
      icon: <SavingsIcon />,
      color: "#b45309",
      bg: "linear-gradient(135deg, #fff7ed 0%, #fffbeb 100%)",
    },
    {
      label: "Deuda por pagar",
      value: formatearNumero(estadoActual.deudaPorPagar),
      helper: "Créditos de caja pendientes",
      icon: <AccountBalanceIcon />,
      color: "#6d28d9",
      bg: "linear-gradient(135deg, #f5f3ff 0%, #faf5ff 100%)",
    },
    {
      label: "Posición neta",
      value: formatearNumero(estadoActual.posicionNeta),
      helper: estadoActual.posicionNeta >= 0 ? "A favor" : "En contra",
      icon: <TrendingUpIcon />,
      color: estadoActual.posicionNeta >= 0 ? "#0f766e" : "#b91c1c",
      bg:
        estadoActual.posicionNeta >= 0
          ? "linear-gradient(135deg, #ecfeff 0%, #f0fdfa 100%)"
          : "linear-gradient(135deg, #fef2f2 0%, #fff1f2 100%)",
    },
  ];

  const ingresosRatio =
    stats.ingresos + stats.egresos > 0
      ? (stats.ingresos / (stats.ingresos + stats.egresos)) * 100
      : 0;

  const totalSeleccionado = useMemo(() => {
    if (!selectedCats.length) return 0;
    return categoriasRango
      .filter((c) => selectedCats.includes(c.id))
      .reduce((acc, c) => acc + (Number(c.neto) || 0), 0);
  }, [categoriasRango, selectedCats]);

  const toggleCategoria = (id) => {
    setSelectedCats((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const seleccionarTodo = () =>
    setSelectedCats(categoriasRango.map((c) => c.id));
  const limpiarSeleccion = () => setSelectedCats([]);

  const addInvest = async () => {
    const cantidadNum = Number(objInversion.cantidad);

    if (!objInversion.raiz) {
      Swal.fire("Falta la inversión", "Selecciona a qué inversión corresponde el movimiento.", "warning");
      return;
    }

    if (!objInversion.tipo) {
      Swal.fire("Falta el tipo", "Selecciona si es un ingreso o un egreso.", "warning");
      return;
    }

    if (!cantidadNum || cantidadNum <= 0) {
      Swal.fire("Cantidad inválida", "Captura una cantidad mayor a cero.", "warning");
      return;
    }

    try {
      setSaving(true);
      const newInversion = {
        ...objInversion,
        cantidad: cantidadNum,
        fecha: objInversion.fecha ? new Date(objInversion.fecha) : new Date(),
        descripcion: (objInversion.descripcion || "").trim(),
      };
      await addDoc(collection(dbFirebase, "inversiones"), newInversion);

      setOpenModal(false);
      setObjInversion(initialObjInversion);
      await fetchData();

      Swal.fire({
        title: "Movimiento agregado con éxito",
        icon: "success",
        confirmButtonText: "Aceptar",
      });
    } catch (e) {
      Swal.fire({
        title: "Movimiento erróneo",
        text: "El movimiento falló con el siguiente error: " + e,
        icon: "error",
        confirmButtonText: "Aceptar",
      });
    } finally {
      setSaving(false);
    }
  };

  // Total ya registrado para la inversión seleccionada en el formulario de rendimiento,
  // y la diferencia contra el total real capturado (positiva = plusvalía, negativa = minusvalía).
  const totalRegistradoRendimiento = totalesPorRaiz[rendimientoForm.raiz] || 0;
  const totalActualRendimientoNum = Number(rendimientoForm.totalActual);
  const diferenciaRendimiento =
    rendimientoForm.raiz &&
    rendimientoForm.totalActual !== "" &&
    !Number.isNaN(totalActualRendimientoNum)
      ? totalActualRendimientoNum - totalRegistradoRendimiento
      : null;

  const addRendimiento = async () => {
    if (!rendimientoForm.raiz) {
      Swal.fire("Falta la inversión", "Selecciona a qué inversión corresponde el rendimiento.", "warning");
      return;
    }

    if (rendimientoForm.totalActual === "" || Number.isNaN(totalActualRendimientoNum)) {
      Swal.fire("Total inválido", "Captura el total actual de la inversión.", "warning");
      return;
    }

    if (diferenciaRendimiento === 0) {
      Swal.fire(
        "Sin variación",
        "El total actual es igual al registrado, no hay plusvalía ni minusvalía que registrar.",
        "info"
      );
      return;
    }

    const esGanancia = diferenciaRendimiento > 0;
    const etiqueta = esGanancia ? "Plusvalía" : "Minusvalía";
    const notaExtra = (rendimientoForm.descripcion || "").trim();
    const descripcion = `${etiqueta} por ajuste de valuación (total actual: ${formatearNumero(
      totalActualRendimientoNum
    )})${notaExtra ? ` — ${notaExtra}` : ""}`;

    try {
      setSaving(true);
      await addDoc(collection(dbFirebase, "inversiones"), {
        fecha: rendimientoForm.fecha ? new Date(rendimientoForm.fecha) : new Date(),
        cantidad: Math.abs(diferenciaRendimiento),
        raiz: rendimientoForm.raiz,
        tipo: esGanancia ? "INGRESO" : "EGRESO",
        descripcion,
        esRendimiento: true,
      });

      setOpenRendimientoModal(false);
      setRendimientoForm(initialRendimientoForm);
      await fetchData();

      Swal.fire({
        title: `${etiqueta} registrada`,
        text: `Se registró ${formatearNumero(Math.abs(diferenciaRendimiento))} como ${etiqueta.toLowerCase()}.`,
        icon: "success",
        confirmButtonText: "Aceptar",
      });
    } catch (e) {
      Swal.fire({
        title: "Rendimiento erróneo",
        text: "El rendimiento falló con el siguiente error: " + e,
        icon: "error",
        confirmButtonText: "Aceptar",
      });
    } finally {
      setSaving(false);
    }
  };

  const lugarColumns = [
    { key: "lugar", header: "Lugar" },
    {
      key: "cantidad",
      header: "Cantidad",
      render: (value) => (
        <Typography
          sx={{
            textAlign: "right",
            fontVariantNumeric: "tabular-nums",
            fontWeight: 600,
            color: value >= 0 ? "#1d4ed8" : "#b91c1c",
          }}
        >
          {formatearNumero(value)}
        </Typography>
      ),
    },
  ];

  const movimientoColumns = [
    { key: "fechaFormateada", header: "Fecha" },
    { key: "lugar", header: "Lugar" },
    {
      key: "tipo",
      header: "Tipo",
      render: (value, row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
          <Typography color={value === "INGRESO" ? "success.main" : "error.main"} sx={{ fontWeight: 600 }}>
            {value}
          </Typography>
          {row.esRendimiento && (
            <Chip size="small" label="Ajuste" sx={{ height: 20, fontSize: 11 }} />
          )}
        </Box>
      ),
    },
    {
      key: "cantidad",
      header: "Cantidad",
      render: (value) => (
        <Typography sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
          {formatearNumero(value)}
        </Typography>
      ),
    },
    { key: "descripcion", header: "Descripción" },
  ];

  // Solo los brokers/inversiones activos se ofrecen como opción al registrar algo nuevo;
  // los inactivos se gestionan en /brokers y siguen apareciendo en el histórico ya capturado.
  const categoriasActivas = categoriasList.filter((c) => c.activo !== false);

  const exportarMovimientos = () => {
    const headers = ["Fecha", "Lugar", "Tipo", "Cantidad", "Origen", "Descripción"];
    const rows = movimientosFiltrados.map((m) => [
      m.fechaFormateada,
      m.lugar,
      m.tipo,
      m.cantidad,
      m.esRendimiento ? "Ajuste de rendimiento" : "Movimiento",
      m.descripcion || "",
    ]);
    exportToCsv(`movimientos_${dayjs().format("YYYY-MM-DD")}.csv`, headers, rows);
  };

  return (
    <>
      <NavbarProject />
      <Box sx={{ p: { xs: 1.5, md: 2 } }}>
        <Box
          sx={{
            borderRadius: 6,
            overflow: "hidden",
            boxShadow: "0 24px 70px rgba(15, 23, 42, 0.10)",
            border: "1px solid rgba(15, 23, 42, 0.06)",
            background: "linear-gradient(135deg, #0f766e 0%, #147a5c 40%, #1d4ed8 100%)",
            color: "white",
            position: "relative",
            px: { xs: 3, md: 5 },
            py: { xs: 4, md: 5 },
            mb: 3,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(120deg, rgba(255,255,255,0.14), transparent 32%, transparent 68%, rgba(255,255,255,0.10))",
              pointerEvents: "none",
            }}
          />
          <Grid container spacing={3} alignItems="center" sx={{ position: "relative" }}>
            <Grid item xs={12} md={8}>
              <Typography variant="overline" sx={{ letterSpacing: 1.8, opacity: 0.85, display: "block" }}>
                Panel de control
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                Dashboard General
              </Typography>
              <Typography
                variant="body1"
                sx={{ maxWidth: 620, opacity: 0.92, mt: 1, fontSize: { xs: 15, md: 17 } }}
              >
                Consulta el flujo de caja, la cartera por cobrar y la deuda de la empresa en un
                solo lugar.
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 4,
                  backgroundColor: "rgba(255,255,255,0.14)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  color: "white",
                  backdropFilter: "blur(8px)",
                }}
              >
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Estado actual
                </Typography>
                <Chip
                  label={stats.saldoNeto >= 0 ? "Superávit" : "Déficit"}
                  sx={{
                    mt: 1,
                    mb: 2,
                    bgcolor: "rgba(255,255,255,0.18)",
                    color: "white",
                    borderRadius: 2,
                  }}
                />
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Ingresos vs. egresos
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {ingresosRatio.toFixed(1)}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={ingresosRatio}
                  sx={{
                    mt: 2,
                    height: 10,
                    borderRadius: 999,
                    bgcolor: "rgba(255,255,255,0.22)",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 999,
                      bgcolor: "#facc15",
                    },
                  }}
                />
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Barra de control: filtros de rango + acción principal, siempre visibles sin tener que bajar */}
        <Paper
          elevation={0}
          sx={{
            ...sectionCardSx,
            p: 2,
            mb: 3,
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: { xs: "stretch", md: "center" },
          }}
        >
          <Grid container spacing={2} alignItems="center" sx={{ flex: 1 }}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="preset-label">Rango</InputLabel>
                <Select
                  labelId="preset-label"
                  label="Rango"
                  value={preset}
                  onChange={(e) => setPreset(e.target.value)}
                >
                  <MenuItem value="mes">Mes actual</MenuItem>
                  <MenuItem value="3m">Ultimos 3 meses</MenuItem>
                  <MenuItem value="6m">Ultimos 6 meses</MenuItem>
                  <MenuItem value="12m">Ultimos 12 meses</MenuItem>
                  <MenuItem value="ytd">Año en curso (YTD)</MenuItem>
                  <MenuItem value="todo">Todo</MenuItem>
                  <MenuItem value="custom">Personalizado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {preset === "custom" && (
              <>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Desde"
                    InputLabelProps={{ shrink: true }}
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Hasta"
                    InputLabelProps={{ shrink: true }}
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                  />
                </Grid>
              </>
            )}
          </Grid>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setOpenRendimientoModal(true)}
              disabled={saving}
              sx={{ whiteSpace: "nowrap" }}
            >
              Registrar rendimiento
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={() => setOpenModal(true)}
              disabled={saving}
              sx={{ whiteSpace: "nowrap" }}
            >
              Agregar movimiento
            </Button>
          </Box>
        </Paper>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={2}>
              {statCards.map((card) => (
                <Grid item xs={12} sm={6} md={3} key={card.label}>
                  <StatCard {...card} />
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Cartera y deuda (estado actual, no depende del rango de fechas)
              </Typography>
              <Grid container spacing={2}>
                {estadoCards.map((card) => (
                  <Grid item xs={12} sm={6} md={4} key={card.label}>
                    <StatCard {...card} />
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Saldo por lugar y libro de movimientos: la información operativa, antes que las gráficas */}
            <Grid container spacing={2} sx={{ mt: 3 }} alignItems="stretch">
              <Grid item xs={12} md={5}>
                <Card sx={{ ...sectionCardSx, height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Saldo por lugar
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                      Movimientos netos por categoría en el rango seleccionado, incluyendo
                      ajustes de rendimiento (plusvalía/minusvalía).
                    </Typography>
                    <TableEdit columns={lugarColumns} rows={lugaresInversion} />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={7}>
                <Card sx={{ ...sectionCardSx, height: "100%" }}>
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Movimientos (rango seleccionado)
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FileDownloadIcon />}
                        onClick={exportarMovimientos}
                        disabled={!movimientosFiltrados.length}
                      >
                        Exportar
                      </Button>
                    </Box>
                    <TableEdit columns={movimientoColumns} rows={movimientosFiltrados} />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Análisis: gráficas agrupadas en una sola sección, lado a lado en pantallas grandes */}
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }} gutterBottom>
                Análisis
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: -1, mb: 2 }}>
                Flujo de caja real: no incluye ajustes de rendimiento (plusvalía/minusvalía).
              </Typography>
              <Grid container spacing={2} alignItems="stretch">
                <Grid item xs={12} md={6}>
                  <Card sx={{ ...sectionCardSx, height: "100%" }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }} gutterBottom>
                        Ingresos vs Egresos por mes
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={datasetMensual} barGap={4} barCategoryGap="24%">
                          <CartesianGrid vertical={false} stroke="#e1e0d9" />
                          <XAxis
                            dataKey="mes"
                            tick={{ fill: "#898781", fontSize: 12 }}
                            axisLine={{ stroke: "#c3c2b7" }}
                            tickLine={false}
                          />
                          <YAxis
                            tick={{ fill: "#898781", fontSize: 12 }}
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={(value) => formatearNumero(value)}
                            width={90}
                          />
                          <Tooltip
                            formatter={(value) => formatearNumero(value)}
                            contentStyle={{
                              borderRadius: 12,
                              border: "1px solid rgba(15, 23, 42, 0.08)",
                              boxShadow: "0 12px 30px rgba(16, 24, 40, 0.12)",
                            }}
                          />
                          <Legend wrapperStyle={{ fontSize: 13 }} />
                          <Bar
                            dataKey="Ingreso"
                            name="Ingreso"
                            fill="#1d4ed8"
                            radius={[4, 4, 0, 0]}
                            maxBarSize={36}
                          />
                          <Bar
                            dataKey="Egreso"
                            name="Egreso"
                            fill="#b91c1c"
                            radius={[4, 4, 0, 0]}
                            maxBarSize={36}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card sx={{ ...sectionCardSx, height: "100%" }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }} gutterBottom>
                        Evolución del Saldo Neto
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={evolucion} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                          <CartesianGrid vertical={false} stroke="#e1e0d9" />
                          <XAxis
                            dataKey="mes"
                            tick={{ fill: "#898781", fontSize: 12 }}
                            axisLine={{ stroke: "#c3c2b7" }}
                            tickLine={false}
                          />
                          <YAxis
                            tick={{ fill: "#898781", fontSize: 12 }}
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={(value) => formatearNumero(value)}
                            width={90}
                          />
                          <Tooltip
                            formatter={(value) => formatearNumero(value)}
                            contentStyle={{
                              borderRadius: 12,
                              border: "1px solid rgba(15, 23, 42, 0.08)",
                              boxShadow: "0 12px 30px rgba(16, 24, 40, 0.12)",
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="saldoNeto"
                            name="Saldo neto"
                            stroke="#0f766e"
                            strokeWidth={2}
                            dot={{ r: 4, strokeWidth: 0, fill: "#0f766e" }}
                            activeDot={{ r: 6 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            {/* Disposición inmediata: herramienta manual, colapsada por defecto para no alargar la página */}
            <Box sx={{ mt: 4 }}>
              <Accordion sx={{ ...sectionCardSx, "&:before": { display: "none" } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Disposición inmediata (selección manual)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      Total seleccionado: {formatearNumero(totalSeleccionado)}
                    </Typography>
                    <Box>
                      <Button size="small" onClick={seleccionarTodo}>
                        Seleccionar todo
                      </Button>
                      <Button size="small" onClick={limpiarSeleccion}>
                        Limpiar
                      </Button>
                    </Box>
                  </Box>

                  <Box sx={{ maxHeight: 240, overflowY: "auto", mt: 1, pr: 1 }}>
                    <FormGroup>
                      {categoriasRango.map((cat) => (
                        <FormControlLabel
                          key={cat.id}
                          control={
                            <Checkbox
                              checked={selectedCats.includes(cat.id)}
                              onChange={() => toggleCategoria(cat.id)}
                            />
                          }
                          label={`${cat.nombre} — ${formatearNumero(cat.neto)}`}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          </>
        )}
      </Box>

      <Backdrop open={saving} sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Dialog open={openModal} maxWidth="xs" fullWidth onClose={() => setOpenModal(false)}>
        <DialogTitle>Agregar movimiento</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Inversión</InputLabel>
            <Select
              value={objInversion.raiz}
              label="Inversión"
              onChange={(event) =>
                setObjInversion((prevState) => ({
                  ...prevState,
                  raiz: event.target.value,
                }))
              }
            >
              {categoriasActivas.map((row) => (
                <MenuItem key={row.id} value={row.id}>
                  {row.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Tipo</InputLabel>
            <Select
              value={objInversion.tipo}
              label="Tipo"
              onChange={(event) =>
                setObjInversion((prevState) => ({
                  ...prevState,
                  tipo: event.target.value,
                }))
              }
            >
              <MenuItem value={"INGRESO"}>INGRESO</MenuItem>
              <MenuItem value={"EGRESO"}>EGRESO</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            sx={{ mt: 2 }}
            label="Cantidad"
            variant="outlined"
            type="number"
            onChange={(evt) =>
              setObjInversion((prevState) => ({
                ...prevState,
                cantidad: evt.target.value,
              }))
            }
          />

          <TextField
            fullWidth
            sx={{ mt: 2 }}
            InputLabelProps={{ shrink: true }}
            label="Fecha"
            variant="outlined"
            type="datetime-local"
            value={objInversion.fecha}
            onChange={(evt) =>
              setObjInversion((prevState) => ({
                ...prevState,
                fecha: evt.target.value,
              }))
            }
          />
          <TextField
            fullWidth
            multiline
            minRows={3}
            sx={{ mt: 2 }}
            InputLabelProps={{ shrink: true }}
            label="Descripción"
            variant="outlined"
            value={objInversion.descripcion}
            onChange={(evt) =>
              setObjInversion((prevState) => ({
                ...prevState,
                descripcion: evt.target.value,
              }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)} disabled={saving}>
            Cerrar
          </Button>
          <Button onClick={addInvest} variant="contained" disabled={saving}>
            {saving ? "Guardando..." : "Agregar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openRendimientoModal}
        maxWidth="xs"
        fullWidth
        onClose={() => setOpenRendimientoModal(false)}
      >
        <DialogTitle>Registrar rendimiento</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Captura el total actual que te reporta el banco o broker. El sistema compara ese total
            contra lo ya registrado y calcula automáticamente la plusvalía o minusvalía.
          </Typography>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Inversión</InputLabel>
            <Select
              value={rendimientoForm.raiz}
              label="Inversión"
              onChange={(event) =>
                setRendimientoForm((prevState) => ({
                  ...prevState,
                  raiz: event.target.value,
                }))
              }
            >
              {categoriasActivas.map((row) => (
                <MenuItem key={row.id} value={row.id}>
                  {row.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {rendimientoForm.raiz && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Total registrado actualmente: {formatearNumero(totalRegistradoRendimiento)}
            </Typography>
          )}

          <TextField
            fullWidth
            sx={{ mt: 2 }}
            label="Total actual (según el banco/broker)"
            variant="outlined"
            type="number"
            value={rendimientoForm.totalActual}
            onChange={(evt) =>
              setRendimientoForm((prevState) => ({
                ...prevState,
                totalActual: evt.target.value,
              }))
            }
          />

          {diferenciaRendimiento !== null && (
            <Chip
              sx={{ mt: 2 }}
              label={`${diferenciaRendimiento >= 0 ? "Plusvalía" : "Minusvalía"}: ${formatearNumero(
                Math.abs(diferenciaRendimiento)
              )}`}
              color={diferenciaRendimiento >= 0 ? "success" : "error"}
            />
          )}

          <TextField
            fullWidth
            sx={{ mt: 2 }}
            InputLabelProps={{ shrink: true }}
            label="Fecha"
            variant="outlined"
            type="datetime-local"
            value={rendimientoForm.fecha}
            onChange={(evt) =>
              setRendimientoForm((prevState) => ({
                ...prevState,
                fecha: evt.target.value,
              }))
            }
          />

          <TextField
            fullWidth
            multiline
            minRows={2}
            sx={{ mt: 2 }}
            InputLabelProps={{ shrink: true }}
            label="Nota adicional (opcional)"
            variant="outlined"
            value={rendimientoForm.descripcion}
            onChange={(evt) =>
              setRendimientoForm((prevState) => ({
                ...prevState,
                descripcion: evt.target.value,
              }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRendimientoModal(false)} disabled={saving}>
            Cerrar
          </Button>
          <Button onClick={addRendimiento} variant="contained" disabled={saving}>
            {saving ? "Guardando..." : "Registrar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Dashboard;
